import type { FormFieldDescriptor, ResolvedFormOption } from '@finance/contracts';
import { formatValue, createFormatDescriptor } from '../formatting/index';
import { maskValue, shouldMaskInDisplay } from './sensitive-value-policy';

function resolveOptionLabels(
  value: unknown,
  options: ResolvedFormOption[] | undefined,
): string | undefined {
  if (!options || options.length === 0) return undefined;
  if (Array.isArray(value)) {
    const selected = value
      .map((v) => options.find((opt) => String(opt.value) === String(v))?.label)
      .filter((label): label is string => label !== undefined);
    return selected.length > 0 ? selected.join(', ') : undefined;
  }
  return options.find((opt) => String(opt.value) === String(value))?.label;
}

export function formatDecimal(value: unknown, fractionDigits = 2): string {
  return formatValue(
    value,
    createFormatDescriptor('decimal', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })
  );
}

export function formatMoney(value: unknown): string {
  return formatValue(value, createFormatDescriptor('currency'));
}

export function formatNumber(value: unknown): string {
  return formatValue(value, createFormatDescriptor('integer'));
}

export function formatDate(value: unknown): string {
  return formatValue(value, createFormatDescriptor('date'));
}

export function formatDateTime(value: unknown): string {
  return formatValue(value, createFormatDescriptor('datetime'));
}

export function formatYesNo(value: unknown): string {
  return value === true ? 'Yes' : value === false ? 'No' : '';
}

export function formatFieldValue(
  field: FormFieldDescriptor,
  value: unknown,
  options?: ResolvedFormOption[],
): string {
  if (value === undefined || value === null) return '';
  if (shouldMaskInDisplay(field)) return maskValue(value);
  switch (field.fieldType) {
    case 'number':
      return formatNumber(value);
    case 'decimal':
      return formatDecimal(value);
    case 'percentage':
      return formatValue(value, createFormatDescriptor('percentage'));
    case 'quantity':
      return formatDecimal(value, 3);
    case 'money':
      return formatMoney(value);
    case 'date':
      return formatDate(value);
    case 'datetime':
      return formatDateTime(value);
    case 'checkbox':
    case 'switch':
      return formatYesNo(value);
    case 'select':
    case 'multiselect':
      return resolveOptionLabels(value, options) ?? String(value);
    default:
      return String(value);
  }
}
